import React, { useContext } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { Trans } from '@lingui/react';
import { t } from '@lingui/macro';
import { UrlObject } from 'url';
import { parse } from 'date-fns/parse';
import { isPast } from 'date-fns/isPast';
import { format } from 'date-fns/format';
import { sv, enUS } from 'date-fns/locale';
import { Box, Container, Flex } from '@components/layout/Grid';
import {
  Title,
  SubTitle,
  CategoryTitle,
  TextWrapper,
  TextWrapperInner,
  VideoWrapper,
  Disclaimer,
} from '@ecosystems/landing_pages/hero/components';
import Hero, { Thumbnail, titleAs } from '@ecosystems/landing_pages/hero/Hero';
import { hexToRgbA } from '@lib/utils';
import SvgCircleCheckmark from '@assets/CircleCheckmark';
import Icon from '@components/Icon';
import LanguageContext from '@lib/contexts/languageContext';
import { Button } from '@components/ButtonV2';
import { BundleAccessState } from '@pages/[lang]/c/[client]/b/[slug]';
import { WaitlistCountdown } from './WaitlistCountdown';
import IconTooltip from '@components/IconTooltip';
import { JoinBundleWaitlist } from '@ecosystems/buttons/JoinBundleWaitlist';
import {
  BundleCategoryKind,
  BundleEssentialFieldsFragment,
  BundleQuery,
} from '@gql/generated';
import { BundleExtendAccessMenu } from './BundleExtendAccessMenu';
import { UserContext } from '@lib/contexts/UserProvider';

const Section = styled(Hero)`
  position: relative;
  flex-direction: column;
  width: 100%;

  ${TextWrapper} {
    width: 100%;
    padding: 20px 0 0;
    margin: 0 auto;
  }

  ${VideoWrapper} {
    width: 100%;

    ${Thumbnail} {
      aspect-ratio: 3/2;
      object-fit: cover;
      object-position: center center;
    }
  }

  ${Disclaimer} {
    font-style: normal;
    margin-top: 30px;

    ul {
      display: flex;
      flex-direction: column;
      align-items: center;
      list-style: none;
      color: white;
      font-size: 16px;
      font-weight: 600;
      padding-left: 0;

      li {
        display: flex;
        align-items: center;
        margin-top: 10px;

        svg {
          fill: white;
          height: 14px;
          margin-right: 10px;
          margin-top: 4px;
        }
      }
    }
  }

  ${CategoryTitle} {
    display: flex;
    flex-direction: column;
    font-size: 14px;
    font-weight: 600;
    line-height: 17px;
    margin-top: 20px;
    margin-bottom: 15px;

    .logos {
      display: flex;
      align-items: center;

      .separator {
        font-weight: 300;
        margin: 0 6px;
      }

      i:before {
        font-size: 18px;
      }

      img {
        height: 20px;
      }
    }

    .clientText {
      display: flex;
      margin-top: 20px;

      svg {
        margin-right: 5px;
      }

      i {
        margin-left: 5px;
        cursor: pointer;
      }
    }
  }

  ${Title} {
    font-size: 28px;
    font-weight: bold;
    line-height: 1.4;
  }

  ${SubTitle} {
    max-width: 560px;
    font-size: 16px;
    line-height: 1.3;
  }

  @media (min-width: ${({ theme }) => theme.viewports.tablet}px) {
    flex-direction: row;

    ${TextWrapper} {
      width: 50%;
      padding-left: 40px;

      ${TextWrapperInner} {
        margin: 0 auto;

        ${Title} {
          font-size: 32px;
        }
      }
    }

    ${VideoWrapper} {
      width: 50%;
      height: 100%;
      padding: 0 40px 0 0;
    }

    ${Disclaimer} {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      font-style: normal;
      margin-bottom: 0;
      padding: 14px 20px;
      background-color: rgba(17, 158, 221, 0.2);

      ul {
        max-width: 860px;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        margin: 0 auto;

        li {
          margin-top: 0;
          margin-right: 50px;
        }
      }
    }
  }
`;

const GetAccessButton = styled(Button)`
  width: 100%;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.secondary.blue};

  :first-letter {
    text-transform: capitalize;
  }

  :hover {
    background-color: ${({ theme }) =>
      hexToRgbA(theme.colors.secondary.blue, 0.9)};
  }

  @media (min-width: ${({ theme }) => theme.viewports.tablet}px) {
    width: auto;
  }
`;

const AccessUntilText = styled.span`
  display: flex;
  align-items: flex-start;
  font-size: 14px;
  font-style: italic;
  line-height: 20px;

  svg {
    margin-right: 5px;
  }
`;

const Waitlist = styled.div`
  min-width: clamp(60%, 400px, 500px);
  border-top: 1px solid lightgrey;
  padding-top: 30px;
`;

const InformationText = styled(AccessUntilText)`
  display: inline-block;
  align-items: center;
  font-style: normal;
  margin-bottom: 15px;

  i {
    display: inline-block;
    margin-right: 4px;
    transform: translateY(2px);
  }
`;

const Price = styled.p`
  font-size: 16px;
  font-weight: 800;
  margin-top: 0;
`;

const OldPrice = styled(Price)`
  font-weight: 400;
  text-decoration: line-through;
  margin-left: 10px;
`;

const dateLocal = {
  se: sv,
  fi: sv,
  no: enUS,
  en: enUS,
};

const AccessStatus = (props: { bundle: BundleEssentialFieldsFragment }) => {
  const { bundle } = props;
  const lang = useContext(LanguageContext);
  let accessUntil = format(
    new Date(bundle?.userAccess?.accessUntil),
    'dd MMMM yyyy',
    {
      locale: dateLocal[lang],
    }
  );

  return (
    <>
      <Flex
        width={1}
        key="0"
        justifyContent="space-between"
        alignItems="center"
      >
        <AccessUntilText>
          <SvgCircleCheckmark />
          <Trans
            id="bundle.id_page.hero.access_until_text"
            values={{ accessUntil }}
          />
        </AccessUntilText>
        <BundleExtendAccessMenu bundle={bundle} />
      </Flex>
    </>
  );
};

type Props = {
  image?: string;
  video?: React.ReactNode;
  title: React.ReactNode;
  titleAs: titleAs;
  subtitle: React.ReactNode;
  noSubtitleWrapper?: boolean;
  category?: React.ReactNode;
  //
  bundle: BundleQuery['bundle'];
  getStartedUrl: UrlObject;
  userAccessState: BundleAccessState;
};

export const BundleHeroSection = (props: Props) => {
  const lang = useContext(LanguageContext);
  const router = useRouter();
  const {
    bundle,
    image,
    video,
    category,
    title,
    subtitle,
    getStartedUrl,
    userAccessState,
  } = props;

  const [currentUser] = useContext(UserContext);

  if (!bundle) {
    return null;
  }

  let actions = [];
  if (bundle) {
    const startDate = bundle?.waitingItemInfo?.startDate
      ? parse(bundle?.waitingItemInfo?.startDate, 'yyyy-MM-dd', new Date())
      : null;
    if (
      bundle?.waitingItemInfo?.waitingListEnabled &&
      startDate &&
      !isPast(startDate)
    ) {
      const countdown = (
        <>
          <InformationText>
            <Trans
              id="bundle.id_page.hero.waitlist_label"
              values={{ date: format(startDate, 'dd/MM/yyy') }}
            />{' '}
            &nbsp;{' '}
            <IconTooltip
              translationId={t`bundle.id_page.hero.waitlist_description_tooltip`}
            />
          </InformationText>
          <WaitlistCountdown
            waitEndDate={bundle?.waitingItemInfo?.waitingListEndDate}
          />
        </>
      );
      if (bundle?.waitingItemInfo?.waitingListJoined) {
        actions = [
          <Waitlist>
            {countdown}
            <InformationText>
              <Icon fontSize="14px" name="ygb-icon-infob" color="gray" />
              <Trans
                id="bundle.id_page.hero.you_already_joined_waitlist"
                values={{
                  date: format(startDate, 'dd/MM/yyy'),
                  bundle_name: bundle?.title,
                }}
              />
            </InformationText>
          </Waitlist>,
        ];
      } else {
        //
        actions = [
          <Waitlist>
            {countdown}
            <JoinBundleWaitlist
              bundle={{
                id: bundle?.id,
                title: bundle?.title,
                slug: bundle?.slug,
                client_name: bundle?.client?.name,
                bundleCategory: bundle?.bundleCategory,
              }}
            />
          </Waitlist>,
        ];
      }
    } else if (userAccessState === BundleAccessState.Active) {
      // dont want the last else to execute
      if (bundle?.bundleCategory !== BundleCategoryKind['ContentHub']) {
        actions = [<AccessStatus key="0" bundle={bundle} />];
      }
    } else if (
      userAccessState === BundleAccessState.Expired &&
      bundle?.bundleCategory === BundleCategoryKind['ContentHub']
    ) {
      actions = [
        <Box my={20}>
          <GetAccessButton
            href={{
              pathname: `/${lang}/subscription/select`,
              query: {
                to: router.asPath,
                ...(bundle?.specialOfferCode
                  ? { code: bundle?.specialOfferCode }
                  : {}),
              },
            }}
          >
            <Trans id="metadata.upgrade" />
          </GetAccessButton>
        </Box>,
      ];
    } else if (bundle?.bundleCategory === BundleCategoryKind['ContentHub']) {
      actions = [
        <Box my={20}>
          <GetAccessButton key="0" href={getStartedUrl}>
            <Trans id="bundle.id_page.hero.get_access_btn" />
          </GetAccessButton>
        </Box>,
      ];

      if (bundle.promotionSection && !currentUser?.isPremium) {
        actions.push(
          <Box my={20}>
            <Button
              key="0"
              outline
              onClick={() => {
                const section = document.querySelector('#Section--Promotion');
                section.scrollIntoView({
                  behavior: 'smooth',
                  inline: 'center',
                });
              }}
            >
              <Trans id="bundle.id_page.hero.view_bundle_promotion" />
            </Button>
          </Box>
        );
      }
    } else if (bundle?.price) {
      const amount =
        (bundle.price.discountApplied
          ? bundle.price.discountedPrice
          : bundle.price.originalPrice) / 100;
      actions = [
        <Box my={20}>
          <Flex>
            <Price>
              {parseFloat(amount.toString()).toFixed(2)} {bundle.price.currency}
            </Price>
            {bundle.price.discountApplied ? (
              <OldPrice>
                {bundle.price.originalPrice / 100} {bundle.price.currency}
              </OldPrice>
            ) : null}
          </Flex>
          <GetAccessButton key="0" href={getStartedUrl}>
            <Trans id="bundle.id_page.hero.get_access_btn" />
          </GetAccessButton>
        </Box>,
      ];
    }
  }

  return (
    <Container fullWidth px={20} py={40} style={{ backgroundColor: '#f7f7f7' }}>
      <Container>
        <Section
          titleAs="h1"
          image={image}
          video={video}
          category={category}
          title={title}
          description={subtitle}
          noSubtitleWrapper={props.noSubtitleWrapper}
          actions={actions}
        />
      </Container>
    </Container>
  );
};
